/**
 * Scene setup — Three.js scene, camera, WebGPU renderer, lighting.
 *
 * No ground plane here. v1 put a 200x200 quad in the scene at boot because the
 * arena was whatever the AI had built that minute; v2 draws the floor from the
 * match's `MapDef` in ArenaRenderer.js, so the ground is the same geometry the
 * simulation collides against instead of a decorative slab near it.
 */

import * as THREE from 'three/webgpu';
import { createSkyDome } from './EnvironmentEffects.js';
import { initPostProcessing, initialQuality } from './PostProcessing.js';
import { initScreenEffects } from './vfx/ScreenEffects.js';
import { initFx } from './vfx/fx.js';
import { forceWebGL } from './config.js';

const BG_COLOR = 0x14162e;

/**
 * The light a character is lit by, exported because the arena is not the only
 * thing that draws one.
 *
 * `tools/portraits.js` renders the roster for the shell's character select, and
 * carried its own hand copy of these five constants under a comment claiming
 * they were "matched to SceneSetup.js" — a claim nothing enforced, in a file
 * whose entire purpose is that the picker shows what the arena shows.
 *
 * The key's POSITION is deliberately not here: the arena aims it across ninety
 * units of floor, which over a two-unit body is straight down. Colour,
 * intensity and the fill ratio are what have to agree.
 */
export const CHARACTER_LIGHTING = {
  ambient: 0x7080a0,
  ambientI: 0.38,
  hemiSky: 0xb0c8ff,
  hemiGround: 0x32281e,
  hemiI: 0.5,
  key: 0xfff2dc,
  keyI: 2.1,
  toneMappingExposure: 1.25,
};

export async function createScene({ quality = initialQuality() } = {}) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(BG_COLOR);
  // 0.009 put the far half of a 90-unit arena ~50% into fog — the whole
  // mid-ground melted into one navy wash. At 0.005 the far rim keeps its
  // contrast and the fog only eats the void beyond the zone wall.
  scene.fog = new THREE.FogExp2(BG_COLOR, 0.005);

  // 75 is v1's lens. 72 read as claustrophobic next to it.
  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1200);
  camera.position.set(0, 12, 26);

  // Top-level await: the WebGPU device must exist before anything builds a
  // pipeline, and every render module below assumes an initialised renderer.
  const renderer = new THREE.WebGPURenderer({ antialias: true, forceWebGL });
  await renderer.init();

  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  // Default PCF gives a 2048-map hard stair-step edge at this arena scale;
  // soft PCF reads as "grounded" instead of "decal".
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = CHARACTER_LIGHTING.toneMappingExposure;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  document.getElementById('game').appendChild(renderer.domElement);

  // Cel-shading lives on the key/fill ratio. v1's flat fill (ambient 0.8 +
  // hemi 0.6) buried the toon ramp — every surface sat in the same mid-tone and
  // the arena read as washed out. Fill here is just enough to keep the shade
  // side of a character readable; the warm key carries the shaping.
  scene.add(new THREE.AmbientLight(CHARACTER_LIGHTING.ambient, CHARACTER_LIGHTING.ambientI));
  scene.add(
    new THREE.HemisphereLight(
      CHARACTER_LIGHTING.hemiSky,
      CHARACTER_LIGHTING.hemiGround,
      CHARACTER_LIGHTING.hemiI,
    ),
  );

  const directionalLight = new THREE.DirectionalLight(
    CHARACTER_LIGHTING.key,
    CHARACTER_LIGHTING.keyI,
  );
  directionalLight.position.set(60, 110, 45);
  directionalLight.castShadow = quality.tier === 'high' || quality.tier === 'ultra';
  directionalLight.shadow.mapSize.set(quality.shadowSize, quality.shadowSize);
  // The arena is ~90 units across; the default shadow frustum is 10 and would
  // leave everything outside the middle unshadowed.
  const cam = directionalLight.shadow.camera;
  cam.left = -70;
  cam.right = 70;
  cam.top = 70;
  cam.bottom = -70;
  cam.near = 1;
  cam.far = 260;
  // ~0.07 units/texel at this frustum: without a normal bias the platform
  // sides self-shadow into stripes under the stronger key.
  directionalLight.shadow.normalBias = 0.06;
  scene.add(directionalLight);
  scene.add(directionalLight.target);

  createSkyDome(scene);
  initPostProcessing(renderer, scene, camera, directionalLight, quality);
  initScreenEffects(scene);
  // The VFX kit — and its FIXED pool of point lights — goes into the scene
  // before anything lit is compiled: the light set is part of every material's
  // program key, so a light added later would rebuild every shader in the
  // arena on the frame it appeared. After initPostProcessing, because the pool
  // is sized for the device's max tier.
  initFx(scene, renderer);

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  return { scene, camera, renderer, directionalLight };
}
